import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { FolderKanban, FileText, Clock, Upload, ListChecks, CalendarClock, CheckCircle2 } from "lucide-react";
import { formatDateTime, formatDate } from "@/lib/format";
import { PROJECT_STATUS_META } from "@/lib/format";

function Kpi({ icon: Icon, label, value, accent, testId }) {
  return (
    <Card className="card-lift" data-testid={testId}>
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="text-[11px] uppercase tracking-widest text-muted-foreground font-medium">{label}</p>
            <p className="mt-2 font-heading text-3xl font-bold tabular-nums">{value ?? "—"}</p>
          </div>
          <div className="p-2.5 rounded-xl" style={{ backgroundColor: `${accent}18`, color: accent }}>
            <Icon size={18} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function ClientDashboard() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try { const r = await api.get("/dashboard/stats"); setStats(r.data); }
      catch (_e) { void _e; } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="space-y-8" data-testid="client-dashboard">
      <header>
        <p className="text-xs uppercase tracking-widest text-muted-foreground font-medium">Meu portal</p>
        <h1 className="font-heading text-3xl font-bold mt-1">Bem-vindo(a) ao Portal BAUEN</h1>
        <p className="mt-1 text-sm text-muted-foreground">Acompanhe seus projetos, documentos e status em tempo real.</p>
      </header>

      <section className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {loading ? Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-[110px] rounded-xl" />) : (
          <>
            <Kpi icon={FolderKanban} label="Projetos ativos" value={stats?.active_projects} accent="#114B5F" testId="kpi-active-projects" />
            <Kpi icon={Upload} label="Documentos enviados" value={stats?.docs_sent} accent="#10B981" testId="kpi-docs-sent" />
            <Kpi icon={Clock} label="Pendentes" value={stats?.docs_pending} accent="#F59E0B" testId="kpi-docs-pending" />
          </>
        )}
      </section>

      <section>
        <h2 className="font-heading font-semibold mb-4">Meus projetos</h2>
        {loading ? (
          <div className="grid gap-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-24 rounded-xl" />)}</div>
        ) : stats?.my_projects?.length ? (
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {stats.my_projects.map((p) => {
              const meta = PROJECT_STATUS_META[p.status] || { label: p.status, color: "#64748B" };
              return (
                <button
                  key={p.id}
                  onClick={() => navigate(`/projects/${p.id}`)}
                  data-testid={`client-project-${p.id}`}
                  className="text-left card-lift bg-card border rounded-xl p-5"
                >
                  <p className="font-heading font-semibold truncate">{p.name}</p>
                  {p.code && <p className="text-xs text-muted-foreground font-mono mt-0.5">{p.code}</p>}
                  <div className="mt-3 flex items-center justify-between">
                    <span className="text-[11px] font-medium rounded-full px-2 py-0.5 border" style={{ color: meta.color, borderColor: `${meta.color}40`, backgroundColor: `${meta.color}15` }}>{meta.label}</span>
                    <span className="text-xs text-muted-foreground">{formatDate(p.due_date)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <Card><CardContent className="p-6 text-sm text-muted-foreground">Você ainda não possui projetos.</CardContent></Card>
        )}
      </section>

      <section className="grid gap-6 lg:grid-cols-3">
        <Card className="lg:col-span-1" data-testid="cronograma-widget">
          <CardContent className="p-6">
            <div className="flex items-center gap-2 mb-3">
              <ListChecks size={16} className="text-primary" />
              <h3 className="font-heading font-semibold">Cronograma geral</h3>
            </div>
            {loading ? <Skeleton className="h-16 rounded" /> : (
              <>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-muted-foreground">{stats?.stages_completed ?? 0} de {stats?.stages_total ?? 0} etapas</span>
                  <span className="font-heading text-lg font-bold" data-testid="client-cronograma-percent">{stats?.cronograma_percent ?? 0}%</span>
                </div>
                <Progress value={stats?.cronograma_percent ?? 0} className="h-2" />
                <div className="mt-4 pt-3 border-t">
                  <p className="text-[11px] uppercase tracking-widest text-muted-foreground mb-1">Próxima etapa</p>
                  {stats?.next_stage ? (
                    <div className="flex items-center gap-2 text-sm" data-testid="client-next-stage">
                      <CalendarClock size={14} className="text-muted-foreground" />
                      <span>{stats.next_stage.name}</span>
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground flex items-center gap-2"><CheckCircle2 size={14} className="text-emerald-500" /> Nenhuma etapa pendente</p>
                  )}
                </div>
              </>
            )}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardContent className="p-6">
            <h3 className="font-heading font-semibold mb-4">Últimas atualizações</h3>
            {stats?.recent_history?.length ? (
              <ul className="divide-y">
                {stats.recent_history.map((h) => (
                  <li key={h.id} className="py-3 flex items-start gap-3 text-sm">
                    <span className="w-2 h-2 mt-2 rounded-full bg-primary" />
                    <div className="flex-1">
                      <p>{h.description}</p>
                      <p className="text-xs text-muted-foreground">{h.user_name} • {formatDateTime(h.created_at)}</p>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">Nenhuma atualização recente.</p>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
